'use client';

import { Cross2Icon } from '@radix-ui/react-icons';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { DialogProps as DrawerRootProps } from 'vaul';
import {
  Dialog,
  DialogClose,
  DialogContent as BaseDialogContent,
  DialogDescription as BaseDialogDescription,
  DialogFooter as BaseDialogFooter,
  DialogHeader as BaseDialogHeader,
  DialogTitle as BaseDialogTitle,
  DialogTrigger as BaseDialogTrigger,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerClose,
  DrawerContent as BaseDrawerContent,
  DrawerDescription as BaseDrawerDescription,
  DrawerFooter as BaseDrawerFooter,
  DrawerHeader as BaseDrawerHeader,
  DrawerTitle as BaseDrawerTitle,
  DrawerTrigger as BaseDrawerTrigger,
} from '@/components/ui/drawer';
import {
  Sheet,
  SheetClose,
  SheetContent as BaseSheetContent,
  SheetDescription as BaseSheetDescription,
  SheetFooter as BaseSheetFooter,
  SheetHeader as BaseSheetHeader,
  SheetTitle as BaseSheetTitle,
  SheetTrigger as BaseSheetTrigger,
} from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

type DialogSpecificProps = Omit<
  React.ComponentProps<typeof Dialog>,
  'children' | 'open' | 'defaultOpen' | 'onOpenChange'
>;

type DrawerSpecificProps = Omit<
  DrawerRootProps,
  'children' | 'open' | 'defaultOpen' | 'onOpenChange' | 'fadeFromIndex'
>;

type SheetSpecificProps = Omit<
  React.ComponentProps<typeof Sheet>,
  'children' | 'open' | 'defaultOpen' | 'onOpenChange'
>;

type ResponsiveDialogContextValue = {
  isDrawer: boolean;
  isSheet: boolean;
  sheetSide?: 'top' | 'bottom' | 'left' | 'right';
};

const ResponsiveDialogContext = React.createContext<ResponsiveDialogContextValue | undefined>(
  undefined,
);

const useResponsiveDialogContext = () => {
  const context = React.useContext(ResponsiveDialogContext);

  if (!context) {
    throw new Error('ResponsiveDialog components must be used within <ResponsiveDialog>.');
  }

  return context;
};

type ResponsiveDialogProps = {
  children?: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  mobileVariant?: 'drawer' | 'dialog' | 'sheet';
  /**
   * Force a specific mobile detection state.
   * Useful for testing or storybook environments.
   */
  isMobileOverride?: boolean;
  dialogProps?: DialogSpecificProps;
  drawerProps?: DrawerSpecificProps;
  sheetProps?: SheetSpecificProps;
  /**
   * Side for sheet variant. Defaults to 'right'.
   */
  sheetSide?: 'top' | 'bottom' | 'left' | 'right';
};

const ResponsiveDialog = ({
  children,
  open,
  defaultOpen,
  onOpenChange,
  mobileVariant = 'drawer',
  isMobileOverride,
  dialogProps,
  drawerProps,
  sheetProps,
  sheetSide = 'right',
}: ResponsiveDialogProps) => {
  const detectedIsMobile = useIsMobile();
  const isMobile = isMobileOverride ?? detectedIsMobile;
  const isDrawer = mobileVariant === 'drawer' && isMobile;
  const isSheet = mobileVariant === 'sheet' && isMobile;

  const contextValue = React.useMemo<ResponsiveDialogContextValue>(
    () => ({ isDrawer, isSheet, sheetSide }),
    [isDrawer, isSheet, sheetSide],
  );

  if (isDrawer) {
    return (
      <ResponsiveDialogContext.Provider value={contextValue}>
        <Drawer open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} {...drawerProps}>
          {children}
        </Drawer>
      </ResponsiveDialogContext.Provider>
    );
  }

  if (isSheet) {
    return (
      <ResponsiveDialogContext.Provider value={contextValue}>
        <Sheet open={open} onOpenChange={onOpenChange} {...sheetProps}>
          {children}
        </Sheet>
      </ResponsiveDialogContext.Provider>
    );
  }

  return (
    <ResponsiveDialogContext.Provider value={contextValue}>
      <Dialog open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} {...dialogProps}>
        {children}
      </Dialog>
    </ResponsiveDialogContext.Provider>
  );
};

type ResponsiveDialogTriggerProps = React.ComponentPropsWithoutRef<typeof BaseDialogTrigger>;

const ResponsiveDialogTrigger = React.forwardRef<
  React.ComponentRef<typeof BaseDialogTrigger>,
  ResponsiveDialogTriggerProps
>((props, ref) => {
  const { isDrawer, isSheet } = useResponsiveDialogContext();

  if (isDrawer) {
    return <BaseDrawerTrigger ref={ref} {...props} />;
  }

  if (isSheet) {
    return <BaseSheetTrigger ref={ref} {...props} />;
  }

  return <BaseDialogTrigger ref={ref} {...props} />;
});
ResponsiveDialogTrigger.displayName = 'ResponsiveDialogTrigger';

type ResponsiveDialogCloseProps = React.ComponentPropsWithoutRef<typeof DialogClose>;

const ResponsiveDialogClose = React.forwardRef<
  React.ComponentRef<typeof DialogClose>,
  ResponsiveDialogCloseProps
>((props, ref) => {
  const { isDrawer, isSheet } = useResponsiveDialogContext();

  if (isDrawer) {
    return <DrawerClose ref={ref} {...props} />;
  }

  if (isSheet) {
    return <SheetClose ref={ref} {...props} />;
  }

  return <DialogClose ref={ref} {...props} />;
});
ResponsiveDialogClose.displayName = 'ResponsiveDialogClose';

type DialogContentProps = React.ComponentPropsWithoutRef<typeof BaseDialogContent>;

const ResponsiveDialogContent = React.forwardRef<
  React.ComponentRef<typeof BaseDialogContent>,
  DialogContentProps
>(({ className, children, style, ...props }, ref) => {
  const { isDrawer, isSheet, sheetSide = 'right' } = useResponsiveDialogContext();
  const { t } = useTranslation('common');

  if (isDrawer) {
    const drawerClassName = cn(
      'border-t border-border bg-background w-full max-w-none sm:w-full sm:max-w-none',
      className,
    );
    const drawerStyle: React.CSSProperties = {
      width: '100vw',
      maxWidth: '100vw',
      ...style,
    };

    return (
      <BaseDrawerContent
        ref={ref as React.Ref<React.ComponentRef<typeof BaseDrawerContent>>}
        className={drawerClassName}
        style={drawerStyle}
        {...props}
      >
        <div className="relative flex max-h-[85dvh] max-h-[85vh] flex-col gap-4 overflow-y-auto px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom)+3rem)] sm:pb-4">
          <DrawerClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <Cross2Icon className="h-4 w-4" />
            <span className="sr-only">{t('close')}</span>
          </DrawerClose>
          <div className="pt-6">{children}</div>
        </div>
      </BaseDrawerContent>
    );
  }

  if (isSheet) {
    const sheetClassName = cn('flex flex-col w-full max-w-none sm:max-w-sm p-0', className);
    const sheetStyle: React.CSSProperties = {
      width: '100vw',
      maxWidth: '100vw',
      ...style,
    };

    return (
      <BaseSheetContent
        ref={ref as React.Ref<React.ComponentRef<typeof BaseSheetContent>>}
        side={sheetSide}
        className={sheetClassName}
        style={sheetStyle}
        {...props}
      >
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom)+3rem)] sm:pb-6">
            {children}
          </div>
        </div>
      </BaseSheetContent>
    );
  }

  return (
    <BaseDialogContent ref={ref} className={className} style={style} {...props}>
      {children}
    </BaseDialogContent>
  );
});
ResponsiveDialogContent.displayName = 'ResponsiveDialogContent';

type DialogHeaderProps = React.ComponentPropsWithoutRef<typeof BaseDialogHeader>;

const ResponsiveDialogHeader = ({ className, ...props }: DialogHeaderProps) => {
  const { isDrawer, isSheet } = useResponsiveDialogContext();

  if (isDrawer) {
    return <BaseDrawerHeader className={className} {...props} />;
  }

  if (isSheet) {
    return <BaseSheetHeader className={className} {...props} />;
  }

  return <BaseDialogHeader className={className} {...props} />;
};
ResponsiveDialogHeader.displayName = 'ResponsiveDialogHeader';

type DialogFooterProps = React.ComponentPropsWithoutRef<typeof BaseDialogFooter>;

const ResponsiveDialogFooter = ({ className, ...props }: DialogFooterProps) => {
  const { isDrawer, isSheet } = useResponsiveDialogContext();

  if (isDrawer) {
    return <BaseDrawerFooter className={className} {...props} />;
  }

  if (isSheet) {
    return <BaseSheetFooter className={className} {...props} />;
  }

  return <BaseDialogFooter className={className} {...props} />;
};
ResponsiveDialogFooter.displayName = 'ResponsiveDialogFooter';

type DialogTitleProps = React.ComponentPropsWithoutRef<typeof BaseDialogTitle>;

const ResponsiveDialogTitle = React.forwardRef<
  React.ComponentRef<typeof BaseDialogTitle>,
  DialogTitleProps
>((props, ref) => {
  const { isDrawer, isSheet } = useResponsiveDialogContext();

  if (isDrawer) {
    return <BaseDrawerTitle ref={ref} {...props} />;
  }

  if (isSheet) {
    return <BaseSheetTitle ref={ref} {...props} />;
  }

  return <BaseDialogTitle ref={ref} {...props} />;
});
ResponsiveDialogTitle.displayName = 'ResponsiveDialogTitle';

type DialogDescriptionProps = React.ComponentPropsWithoutRef<typeof BaseDialogDescription>;

const ResponsiveDialogDescription = React.forwardRef<
  React.ComponentRef<typeof BaseDialogDescription>,
  DialogDescriptionProps
>((props, ref) => {
  const { isDrawer, isSheet } = useResponsiveDialogContext();

  if (isDrawer) {
    return <BaseDrawerDescription ref={ref} {...props} />;
  }

  if (isSheet) {
    return <BaseSheetDescription ref={ref} {...props} />;
  }

  return <BaseDialogDescription ref={ref} {...props} />;
});
ResponsiveDialogDescription.displayName = 'ResponsiveDialogDescription';

export {
  ResponsiveDialog,
  ResponsiveDialogTrigger,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogClose,
};
